/**
 * Result activation for AI search: the `LinkClick` event, the contract-valid
 * click-join payload, the pending-row acknowledgement, and entity navigation.
 *
 * graft/A6: extracted verbatim from AiSearch.tsx. What the surface keeps is the
 * decision of what happens AFTER a result is activated — the dropdown schedules
 * its own dismissal, a routed page does not — which is the `onResultActivated`
 * callback.
 */
import { useCallback, useContext, useState } from "react";
import { useHistory, useRouteMatch } from "react-router";

import { type BuilderInfo } from "helpers/AppProvider.types";
import { SpaInfoContext } from "helpers/globalContext/SPAInfoContext";

import { getEntityLink, navigateEntityLink } from "../AiSearchResults";
import { buildClickEvent } from "../clickTelemetry";
import { getHitKey, normalizeKey } from "../entityMeta";
import type { IAiSearchState, ICategorizedHit } from "../types";
import type { SearchTrackPhase, TrackEvent } from "./useAiSearchTelemetry";

interface IUseAiSearchNavigationArgs {
    state: IAiSearchState;
    /** The live input value — reported verbatim on the legacy LinkClick fields. */
    query: string;
    /** The full visible list in painted order — committed rows then appended. */
    navigableResults: ICategorizedHit[];
    /** Previous search's rows, shown faded while a new search loads. */
    staleResults: ICategorizedHit[];
    /** Visible count: committed rows plus appended rows. */
    resultCount: number;
    builderInfo?: BuilderInfo | null;
    trackEvent: TrackEvent;
    trackSearch: (searchPhase: SearchTrackPhase) => void;
    /** Marks the search as clicked so it is never counted as abandoned. */
    markClicked: () => void;
    /**
     * Fired after the click has been recorded and the pending acknowledgement
     * set. The dropdown uses it to schedule its close; a page surface can omit
     * it entirely.
     */
    onResultActivated?: () => void;
}

export interface IAiSearchNavigation {
    /** Key of the row showing a pending acknowledgement, or null. */
    pendingKey: string | null;
    clearPendingKey: () => void;
    /** Record a result activation. Does NOT navigate — see navigateToIndex. */
    handleResultClick: (
        hit: ICategorizedHit,
        index: number,
        navMethod?: "click" | "keyboard",
    ) => void;
    /** Resolve the row at `index` to an entity link, navigate, and record the click. */
    navigateToIndex: (index: number) => void;
}

export function useAiSearchNavigation(args: IUseAiSearchNavigationArgs): IAiSearchNavigation {
    const {
        state,
        query,
        navigableResults,
        staleResults,
        resultCount,
        builderInfo,
        trackEvent,
        trackSearch,
        markClicked,
        onResultActivated,
    } = args;

    const history = useHistory();
    const match = useRouteMatch();
    const spaContext = useContext(SpaInfoContext);

    const [pendingKey, setPendingKey] = useState<string | null>(null);

    const clearPendingKey = useCallback(() => setPendingKey(null), []);

    const handleResultClick = useCallback(
        (hit: ICategorizedHit, index: number, navMethod: "click" | "keyboard" = "click") => {
            if (state.phase !== "done" && state.phase !== "empty") {
                trackSearch("interrupted");
            }
            // Mark this search as having a click so it isn't later counted as abandoned.
            markClicked();

            // Attribute the click to the search whose results the user was
            // actually shown. A click on a faded stale row (previous search's
            // results displayed while a new search loads) belongs to the
            // PREVIOUS search — its id, trace, query, and shown-list — not to
            // the one currently in flight. Staleness is derived from which
            // list the clicked row is actually rendered from (identity check
            // against the memoized stale list) rather than from the call
            // site, so every entry path — mouse, keyboard, future ones —
            // attributes correctly by construction.
            const isStale = state.staleSearchContext !== null && staleResults[index] === hit;
            const stale = isStale ? state.staleSearchContext : null;
            // The shown list is what the user actually sees, top to bottom:
            // committed rows then appended rows (navigableResults) — the SAME
            // concatenation AiSearchResults renders and keyboard nav walks,
            // and the same order `index` addresses (AiSearchResults passes the
            // overall index for appended rows). On the legacy path
            // appendedResults is always empty, so this equals `results`.
            const visible = isStale ? staleResults : navigableResults;
            // Contract-valid result-click payload (contracts/click-event.schema.json,
            // canonical: BTNet-Search docs/contracts/click-event.schema.json).
            // The server-side click-join turns it into training triples, keyed
            // by trace_id when the trace event arrived, else by search_id.
            // Emitted EVEN when the trace never arrived (trace_id null) and
            // after aborts — the shown results remain clickable either way.
            // Null only when the resolved search_id fails the server
            // sanitizer's pattern (an unjoinable event is more useful absent
            // than malformed); the legacy LinkClick fields still fire.
            const clickEvent = buildClickEvent({
                searchId: stale?.searchId ?? state.canonicalSearchId ?? state.searchId,
                traceId: isStale ? (stale?.traceId ?? null) : state.traceId,
                hit,
                index,
                visible,
                query: stale?.query ?? state.searchQuery,
                builderId: builderInfo?.builderId,
            });

            trackEvent({
                event: "LinkClick",
                element: "Link",
                uniqueId: `${normalizeKey(hit.entity)}`,
                extraInfo: {
                    searchId: state.searchId,
                    searchQuery: query,
                    searchResultRank: index + 1,
                    searchResultId: hit.id,
                    searchResultTitle: hit.summary,
                    // Legacy 278622 field — folding here is intentional and
                    // stays. Only clickEvent.entity must remain verbatim
                    // (the join matches it with plain equality).
                    searchResultType: normalizeKey(hit.entity),
                    searchResultScore: hit.score,
                    searchResultRerankScore: hit.rerank_score,
                    resultCount,
                    timeToClickMs:
                        state.searchStartTime != null
                            ? Date.now() - state.searchStartTime
                            : undefined,
                    navMethod,
                    ...(clickEvent !== null && { clickEvent }),
                },
            });

            // Show a brief pending acknowledgement on the clicked row so the
            // user sees the click registered even though the entity modal takes
            // 1-2s to mount.
            setPendingKey(getHitKey(hit));
            onResultActivated?.();
        },
        [
            trackSearch,
            trackEvent,
            markClicked,
            query,
            onResultActivated,
            navigableResults,
            staleResults,
            builderInfo,
            state.phase,
            state.searchId,
            state.searchQuery,
            state.searchStartTime,
            state.traceId,
            state.canonicalSearchId,
            state.staleSearchContext,
            resultCount,
        ],
    );

    const navigateToIndex = useCallback(
        (index: number) => {
            if (index >= 0 && index < navigableResults.length) {
                const hit = navigableResults[index];
                const link = getEntityLink(hit, builderInfo ?? undefined);
                if (!link) return;

                navigateEntityLink(link, history, match.url, !!spaContext.isSpa);
                handleResultClick(hit, index, "keyboard");
            }
        },
        [navigableResults, handleResultClick, history, match.url, spaContext.isSpa, builderInfo],
    );

    return { pendingKey, clearPendingKey, handleResultClick, navigateToIndex };
}
